import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tag,
  Progress,
  List,
  Modal,
  message,
  Empty,
  Spin,
  Alert,
  Grid,
  Input,
  Form,
  Tooltip,
  Space,
  Select,
  notification
} from "antd";
import {
  GoogleMap,
  Marker,
  Polyline,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  CheckCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  startRouteSessionApi,
  getActiveRouteSessionApi,
  checkinRoutePointApi,
  checkoutRoutePointApi,
  completeRouteSessionApi,
  getRouteProgressApi,
  getMyActiveRouteAssignmentApi,
  checkinAdhocVisitApi,
  checkoutAdhocVisitApi,
  getActiveAdhocVisitApi,
  getAdhocVisitsApi,
  markRouteNotExecutedApi,
  getRoutePointVisitHistoryApi,
} from "../../../apiservice/hrpayroll";
import { GOOGLE_MAP_LIBRARIES } from "../../../constants/googleMaps";
import {
  calculateDistanceMeters,
  formatToCompanyTimezone,
} from "../../../lib/functions";
import {
  fetchMyActiveRouteAssignment,
  fetchRouteAssignments,
} from "../workRoutesThunk";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const targetStatusStyles = {
  outside: {
    background: "#fff1f0",
    border: "1px solid #ffa39e",
    headerColor: "#cf1322",
  },
  inside: {
    background: "#f6ffed",
    border: "1px solid #b7eb8f",
    headerColor: "#389e0d",
  },
  active: {
    background: "#e6f4ff",
    border: "1px solid #91caff",
    headerColor: "#0958d9",
  },
  completed: {
    background: "#f5f5f5",
    border: "1px solid #d9d9d9",
    headerColor: "#595959",
  },
  pending: {
    background: "#ffffff",
    border: "1px solid #f0f0f0",
    headerColor: "#262626",
  },
};

const RouteExecution = ({ center, zoom }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const authInfo = useSelector((state) => state.auth.auth_info);
  const { assignment } = useSelector((state) => state.workRoutes);
  const isIndia = authInfo?.country === "India";
  // Flexible assignments never truly "finish" -- they're an ongoing pool of
  // points the employee works through at their own pace. "Complete Route"
  // reads like the whole assignment is done forever, which isn't true here;
  // it only closes out today's session, so the wording says that plainly.
  const isFlexibleAssignment = assignment?.schedule_type === "flexible";
  const completeRouteLabel = isFlexibleAssignment
    ? "Complete Today's Route"
    : "Complete Route";

  const [loading, setLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [activeVisitPointId, setActiveVisitPointId] = useState(null);
  const [map, setMap] = useState(null);
  const [completedPointIds, setCompletedPointIds] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [gpsStatus, setGpsStatus] = useState("active");
  const [arrivalPromptOpen, setArrivalPromptOpen] = useState(false);
  const [arrivalPoint, setArrivalPoint] = useState(null);
  const [exitPoint, setExitPoint] = useState(null);
  const [exitPromptOpen, setExitPromptOpen] = useState(false);
  const [lastInsideRadius, setLastInsideRadius] = useState(false);
  const [checkinNotes, setCheckinNotes] = useState("");
  const [checkoutNotes, setCheckoutNotes] = useState("");
  const [adhocModalOpen, setAdhocModalOpen] = useState(false);
  const [adhocCheckoutModalOpen, setAdhocCheckoutModalOpen] = useState(false);
  const [activeAdhocVisit, setActiveAdhocVisit] = useState(null);
  const [adhocExitPromptOpen, setAdhocExitPromptOpen] = useState(false);
  const [adhocCheckoutNotes, setAdhocCheckoutNotes] = useState("");
  const [adhocLastInsideRadius, setAdhocLastInsideRadius] = useState(false);
  const [adhocVisits, setAdhocVisits] = useState([]);
  const [routeCompleteModalOpen, setRouteCompleteModalOpen] = useState(false);
  const [routeCompletedPopupShown, setRouteCompletedPopupShown] =
    useState(false);
  const [notExecutedOpen, setNotExecutedOpen] = useState(false);
  const [executionReason, setExecutionReason] = useState();
  const [notes, setNotes] = useState("");

  // PAGINATED HISTORY MODAL STATES
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pointHistory, setPointHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const watchIdRef = useRef(null);
  const activeAdhocVisitRef = useRef(null);
  const adhocLastInsideRadiusRef = useRef(false);
  const completedPointIdsRef = useRef([]);
  const activeVisitPointIdRef = useRef(null);
  const activeSessionRef = useRef(null);
  const lastInsideRadiusRef = useRef(false);
  const arrivalPromptOpenRef = useRef(false);
  const consecutiveInsideHitsRef = useRef({});
  const [adhocForm] = Form.useForm();
  const [adhocCheckoutForm] = Form.useForm();

  const NavigationIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21.71 2.29a1 1 0 0 0-1.05-.24l-17 7a1 1 0 0 0 .08 1.88l7.09 2.36 2.36 7.09a1 1 0 0 0 .93.68h.05a1 1 0 0 0 .9-.76l7-17a1 1 0 0 0-.36-1.01z" />
    </svg>
  );

  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const ADHOC_VISIT_RADIUS = 100;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const mapContainerStyle = {
    width: "100%",
    height: isMobile ? "280px" : "calc(100vh - 220px)",
    borderRadius: "8px",
  };

  const dispatch = useDispatch();
  const routePoints = useMemo(
    () => activeSession?.points || [],
    [activeSession],
  );

  const currentPoint = useMemo(() => {
    if (!routePoints.length) return null;
    return (
      routePoints.find((p) => !completedPointIds.includes(p.route_point_id)) ||
      routePoints[0]
    );
  }, [routePoints, completedPointIds]);

  const loadAdhocVisits = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await getAdhocVisitsApi({ active_session_id: sessionId });
      setAdhocVisits(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadActiveAdhocVisit = useCallback(async () => {
    try {
      const res = await getActiveAdhocVisitApi();
      if (res.data.data) {
        setActiveAdhocVisit({
          id: res.data.data.id,
          visit_name: res.data.data.visit_name,
          latitude: res.data.data.latitude,
          longitude: res.data.data.longitude,
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  // API STREAM LOADING STRATEGY WITH PAGINATION
  const loadPointHistory = useCallback(
    async (point, page = 1) => {
      try {
        setHistoryLoading(true);
        setSelectedPoint(point); // Keeps tracking point context (e.g., currentPoint)

        const res = await getRoutePointVisitHistoryApi({
          route_point_id: point.route_point_id,
          page: page,
          limit: historyPagination.pageSize,
        });

        // Safely unpack data structure matching your backend refactor response
        const responseData = res?.data?.data || {};
        const historyList = responseData.list || [];
        const totalCount = responseData.total || 0;

        setPointHistory(historyList);
        setHistoryPagination((prev) => ({
          ...prev,
          current: page,
          total: totalCount, // Updates the global counter dynamically
        }));
        setHistoryOpen(true);
      } catch (err) {
        message.error("Failed to load history notes.");
      } finally {
        setHistoryLoading(false);
      }
    },
    [historyPagination.pageSize],
  );

  const startLocationTracking = useCallback(() => {
  if (!navigator.geolocation) return;

  if (watchIdRef.current) {
    navigator.geolocation.clearWatch(watchIdRef.current);
  }

  watchIdRef.current = navigator.geolocation.watchPosition(
    (position) => {
      const { latitude: lat, longitude: lng, accuracy } = position.coords;
      
      // 1. Ignore coarse network pings / initial low-accuracy jitter spikes 
      // if (accuracy > 60) return;

      setCurrentPosition({ lat, lng });
      setGpsStatus("active");

      const currentSession = activeSessionRef.current;
      const points = currentSession?.points || [];
      const completedIds = completedPointIdsRef.current;
      const activeVisitId = activeVisitPointIdRef.current;

      // Ordered Route Handling
      if (currentSession?.is_ordered) {
        points.forEach((point) => {
          const distance = calculateDistanceMeters(
            lat,
            lng,
            Number(point.latitude),
            Number(point.longitude),
          );
          const insideRadius = distance <= Number(point.radius_meters);
          const pid = point.route_point_id;

          if (insideRadius && !completedIds.includes(pid) && !activeVisitId && !arrivalPromptOpenRef.current) {
            // Increment proximity confirmations
            consecutiveInsideHitsRef.current[pid] = (consecutiveInsideHitsRef.current[pid] || 0) + 1;
            
            // Require 2 consecutive accurate GPS ticks to confirm entry
            if (consecutiveInsideHitsRef.current[pid] >= 2) {
              setArrivalPoint(point);
              setArrivalPromptOpen(true);
            }
          } else {
            consecutiveInsideHitsRef.current[pid] = 0; // Reset buffer
          }
        });
      }

      // Active Visit Outgoing Checks
      if (activeVisitId) {
        const activePoint = points.find((p) => p.route_point_id === activeVisitId);
        if (activePoint) {
          const distance = calculateDistanceMeters(
            lat,
            lng,
            Number(activePoint.latitude),
            Number(activePoint.longitude),
          );
          const insideRadius = distance <= Number(activePoint.radius_meters);
          
          if (lastInsideRadiusRef.current && !insideRadius) {
            setExitPoint(activePoint);
            setExitPromptOpen(true);
          }
          setLastInsideRadius(insideRadius);
        }
      }

      // Adhoc Tracker Engine
      const currentAdhocVisit = activeAdhocVisitRef.current;
      if (currentAdhocVisit) {
        const distance = calculateDistanceMeters(
          lat,
          lng,
          Number(currentAdhocVisit.latitude),
          Number(currentAdhocVisit.longitude),
        );
        const insideRadius = distance <= ADHOC_VISIT_RADIUS;
        if (adhocLastInsideRadiusRef.current && !insideRadius) {
          setAdhocExitPromptOpen(true);
        }
        adhocLastInsideRadiusRef.current = insideRadius;
        setAdhocLastInsideRadius(insideRadius);
      }
    },
    () => setGpsStatus("lost"),
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
  );
}, []); // Empty dependency array locks the tracking lifecycle down permanently

  const fetchRouteProgress = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await getRouteProgressApi({ active_session_id: sessionId });
      const progress = res.data.data;
      setCompletedPointIds(progress.completed_point_ids || []);
      setProgressPercentage(progress.completion_percentage || 0);
      if (progress.active_visit) {
        setActiveVisitPointId(progress.active_visit.route_point_id);
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  const fetchSession = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getActiveRouteSessionApi();
      setActiveSession(res.data.data || null);
      if (res.data.data?.id) {
        fetchRouteProgress(res.data.data.id);
        await loadAdhocVisits(res.data.data.id);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [fetchRouteProgress, loadAdhocVisits]);

  useEffect(() => {
    dispatch(fetchMyActiveRouteAssignment());
    fetchSession();
    loadActiveAdhocVisit();
  }, [dispatch, fetchSession, loadActiveAdhocVisit]);

  useEffect(() => {
    if (map && routePoints && routePoints.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      routePoints.forEach((point) => {
        bounds.extend({
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        });
      });
      if (currentPosition) {
        bounds.extend({ lat: currentPosition.lat, lng: currentPosition.lng });
      }
      map.fitBounds(bounds);
    }
  }, [map, routePoints, currentPosition]);

  useEffect(() => {
  // Start tracking once on mount
  startLocationTracking();  
  // Clean up and release the GPS hardware ONLY when the component completely unmounts
  return () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  };
}, [startLocationTracking]);

  useEffect(() => {
    activeAdhocVisitRef.current = activeAdhocVisit;
  }, [activeAdhocVisit]);

  useEffect(() => {
    adhocLastInsideRadiusRef.current = adhocLastInsideRadius;
  }, [adhocLastInsideRadius]);

  useEffect(() => {
    completedPointIdsRef.current = completedPointIds;
  }, [completedPointIds]);
  useEffect(() => {
    activeVisitPointIdRef.current = activeVisitPointId;
  }, [activeVisitPointId]);
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);
  useEffect(() => {
    arrivalPromptOpenRef.current = arrivalPromptOpen;
  }, [arrivalPromptOpen]);
  useEffect(() => {
    lastInsideRadiusRef.current = lastInsideRadius;
  }, [lastInsideRadius]);

  const allPointsCompleted =
    routePoints.length > 0 && completedPointIds.length === routePoints.length;

  useEffect(() => {
    if (
      allPointsCompleted &&
      !activeVisitPointId &&
      !activeAdhocVisit &&
      !routeCompletedPopupShown
    ) {
      setRouteCompletedPopupShown(true);
      setRouteCompleteModalOpen(true);
    }
  }, [
    allPointsCompleted,
    activeVisitPointId,
    activeAdhocVisit,
    routeCompletedPopupShown,
  ]);

  const isInsidePointRadius = (point) => {
    if (!currentPosition || !point) return false;
    const distance = calculateDistanceMeters(
      currentPosition.lat,
      currentPosition.lng,
      Number(point.latitude),
      Number(point.longitude),
    );
    return distance <= Number(point.radius_meters || 100);
  };

  const handleStartRoute = async () => {
    try {
      setLoading(true);
      if (!assignment?.id) return message.error("No route assignment found.");
      await startRouteSessionApi({ assignment_id: assignment.id });
      message.success("Route session started.");
      fetchSession();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to start route.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (point) => {
    // 🛑 GUARD LOCK: Prevent multi-click parallel processing
    if (loading) return;

    if (!isInsidePointRadius(point)) {
      return Modal.warning({
        title: "Outside Radius",
        content: "You must be inside the radius to check in.",
      });
    }
    try {
      setLoading(true);
      await checkinRoutePointApi({
        active_session_id: activeSession.id,
        assignment_id: activeSession.assignment_id,
        route_id: activeSession.route_id,
        employee_id: activeSession.employee_id,
        route_point_id: point.route_point_id,
        institution_id: authInfo?.userInfo?.pro_id,
        notes: checkinNotes,
      });
      setActiveVisitPointId(point.route_point_id);
      message.success("Checked in successfully.");
      setArrivalPromptOpen(false);
      setCheckinNotes("");
      fetchRouteProgress(activeSession.id);
    } catch (err) {
      message.error("Check-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (point) => {
    if (loading) return;
    try {
      setLoading(true);
      await checkoutRoutePointApi({
        active_session_id: activeSession.id,
        route_point_id: point.route_point_id,
        institution_id: authInfo.userInfo.pro_id,
        notes: checkoutNotes,
      });
      setActiveVisitPointId(null);
      message.success("Checked out successfully.");
      setExitPromptOpen(false);
      setLastInsideRadius(false);
      setCheckoutNotes("");
      fetchRouteProgress(activeSession.id);
    } catch (err) {
      message.error("Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRoute = async (checkoutAttendance = false) => {
    try {
      if (activeVisitPointId)
        return message.warning(
          "Please checkout the current route point before completing the route.",
        );
      if (activeAdhocVisit)
        return message.warning(
          "Please checkout the active visit before completing the route.",
        );
      setLoading(true);
      await completeRouteSessionApi({
        active_session_id: activeSession.id,
        checkout_attendance: checkoutAttendance,
      });
      message.success("Route completed.");
      setActiveSession(null);
      setActiveVisitPointId(null);
      setCompletedPointIds([]);
      setProgressPercentage(0);
      await dispatch(fetchRouteAssignments());
      await dispatch(fetchMyActiveRouteAssignment());
      await getActiveRouteSessionApi();
      await fetchRouteProgress();
    } catch (err) {
      message.error("Failed to complete route.");
    } finally {
      setLoading(false);
    }
  };

  // One-off fallback fix for when the continuous watchPosition stream
  // (startLocationTracking) hasn't delivered its first tick yet -- e.g. the
  // page was just opened and the button tapped immediately. Doesn't replace
  // or interfere with the ongoing watch; only used here, as a retry before
  // failing outright.
  const getFreshPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          resolve({ lat, lng });
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
      );
    });

  const handleAdhocCheckin = async () => {
    try {
      if (activeAdhocVisit)
        return message.warning("An adhoc visit is already active.");
      let position = currentPosition;
      if (!position) {
        position = await getFreshPosition();
        if (position) setCurrentPosition(position);
      }
      if (!position)
        return message.error("Current location not available.");
      const values = await adhocForm.validateFields();
      const res = await checkinAdhocVisitApi({
        active_session_id: activeSession?.id,
        assignment_id: assignment?.id,
        route_id: assignment?.route_id,
        visit_name: values.visit_name,
        checkin_notes: values.checkin_notes,
        latitude: position?.lat,
        longitude: position?.lng,
      });
      await loadAdhocVisits(activeSession.id);
      setActiveAdhocVisit({
        id: res.data.data.id,
        visit_name: values.visit_name,
        latitude: position.lat,
        longitude: position.lng,
      });
      setAdhocLastInsideRadius(true);
      adhocLastInsideRadiusRef.current = true;
      message.success("Visit checked in successfully.");
      setAdhocModalOpen(false);
      adhocForm.resetFields();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAdhocCheckout = async () => {
    try {
      const values = await adhocCheckoutForm.validateFields();
      await checkoutAdhocVisitApi({
        adhoc_visit_id: activeAdhocVisit.id,
        checkout_notes: values.checkout_notes,
      });
      setActiveAdhocVisit(null);
      setAdhocCheckoutModalOpen(false);
      adhocCheckoutForm.resetFields();
      await loadAdhocVisits(activeSession.id);
      message.success("Visit checked out successfully.");
    } catch (err) {
      console.error(err);
    }
  };

  const handleAutoAdhocCheckout = async () => {
    await checkoutAdhocVisitApi({
      adhoc_visit_id: activeAdhocVisit.id,
      checkout_notes: adhocCheckoutNotes,
    });
    setActiveAdhocVisit(null);
    setAdhocExitPromptOpen(false);
    setAdhocCheckoutNotes("");
    setAdhocLastInsideRadius(false);
    message.success("Visit checked out successfully.");
  };

  const handleNotExecuted = async () => {
    try {
      await markRouteNotExecutedApi({
        assignment_id: assignment.id,
        route_id: assignment.route_id,
        execution_reason: executionReason,
        notes,
      });
      message.success("Route marked as not executed.");
      setActiveSession(null);
      setNotExecutedOpen(false);
      setExecutionReason(undefined);
      setNotes("");
      await dispatch(fetchRouteAssignments());
      await dispatch(fetchMyActiveRouteAssignment());
      await getActiveRouteSessionApi();
    } catch (err) {
      message.error("Failed to mark execution status");
    }
  };

  const handleRefreshRouteConfig = async () => {
    try {
      const previousOrdered = assignment?.is_ordered;
      console.log(assignment)

      const latestAssignment = await dispatch(
        fetchMyActiveRouteAssignment()
      ).unwrap();

      const latestOrdered = latestAssignment?.is_ordered;

      await fetchSession();
      
      if (previousOrdered !== latestOrdered) {
        notification.info({
          message: "Route Configuration Updated",
          description:
            latestOrdered
              ? "This route has been switched to Ordered mode by an administrator."
              : "This route has been switched to Unordered mode by an administrator.",
          duration: 6,
        });

      } else {
        message.success("Route configuration refreshed");
      }      
        await getActiveRouteSessionApi();

          } catch (err) {
      message.error("Failed to refresh route configuration");
    }
  };

  const isOrderedRoute =
    activeSession?.is_ordered === 1 || activeSession?.is_ordered === true;
  const totalPoints = routePoints.length;
  const completedPoints = completedPointIds.length;
  const missedPoints = totalPoints - completedPoints;
  const completionPercentage = totalPoints
    ? Math.round((completedPoints / totalPoints) * 100)
    : 0;

  const navigateToPoint = (point) => {
    const lat = Number(point.latitude);
    const lng = Number(point.longitude);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, "_blank");
  };

  if (loading && !activeSession) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!activeSession) {
    return (
      <Card style={{ margin: isMobile ? "8px" : "0", borderRadius: "8px" }}>
        <Empty
          description={
            assignment
              ? `Assigned Route: ${assignment.route_name}`
              : "No Route Assignment Found"
          }
        />
        <div
          style={{
            textAlign: "center",
            marginTop: 20,
            maxWidth: "390px",
            margin: "20px auto 0 auto",
          }}
        >
          {assignment?.execution_status === "not_executed" ? (
            <Alert
              type="warning"
              showIcon
              message="Route marked as not executed today"
              description={
                <>
                  <div>Reason: {assignment.execution_reason}</div>
                  {assignment.notes && <div>Notes: {assignment.notes}</div>}
                </>
              }
            />
          ) : assignment?.completed_today ? (
            <Alert
              type="success"
              showIcon
              message="Route completed today"
              description={`Completed at ${formatToCompanyTimezone(assignment.completed_session?.completed_at)}`}
            />
          ) : assignment ? (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Button
                type="primary"
                onClick={handleStartRoute}
                size="large"
                icon={<PlayCircleOutlined />}
                block
                style={{ borderRadius: "6px" }}
              >
                Start Route
              </Button>
              <Button
                danger
                type="text"
                onClick={() => setNotExecutedOpen(true)}
                block
                style={{ borderRadius: "6px" }}
              >
                I Cannot Execute Route Today
              </Button>
            </Space>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="No route assignment available for today."
            />
          )}
        </div>
        <Modal
          open={notExecutedOpen}
          title="Cannot Execute Route"
          onCancel={() => setNotExecutedOpen(undefined)}
          onOk={handleNotExecuted}
        >
          <Select
            placeholder="Reason"
            value={executionReason}
            onChange={setExecutionReason}
            style={{ width: "100%" }}
            options={[
              { label: "Sick Leave", value: "sick_leave" },
              isIndia
                ? { label: "Casual Leave", value: "casual_leave" }
                : { label: "Annual Leave", value: "annual_leave" },
              { label: "Personal Emergency", value: "personal_emergency" },
              { label: "Vehicle Breakdown", value: "vehicle_breakdown" },
              { label: "Emergency Duty", value: "emergency_duty" },
              { label: "Other", value: "other" },
            ]}
          />
          <Input.TextArea
            placeholder="Note"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ marginTop: 12 }}
          />
        </Modal>
      </Card>
    );
  }

  return (
    <div
      className="RouteExecution"
      style={{
        padding: isMobile ? "8px" : "16px",
        background: "#f0f2f5",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        <Alert
          type={gpsStatus === "active" ? "success" : "error"}
          message={
            gpsStatus === "active"
              ? "GPS Engine: Tracking Stream Live"
              : "GPS Signal Lost"
          }
          showIcon
        />
        {activeAdhocVisit && (
          <Alert
            showIcon
            type="warning"
            message={`Active Visit: ${activeAdhocVisit.visit_name}`}
          />
        )}
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={10} lg={9}>
          <Card
            bodyStyle={{ padding: isMobile ? "14px" : "16px" }}
            style={{ borderRadius: "8px", border: "none" }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: 600,
                      flex: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {activeSession.route_name}
                  </Title>
                  </Col>
                  <Col>
                    <Button
                      type="link"
                      size="small"                      
                      icon={<ReloadOutlined />}
                      style={{fontSize:"9px"}}
                      onClick={handleRefreshRouteConfig}
                    >
                      Refresh Route Configuration
                    </Button>
                  </Col>
                </Row>
                  <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>
                    Active
                  </Tag>
                  
                </div>
                <div
                  style={{
                    marginTop: 8,
                    padding: "6px 10px",
                    background: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <Text
                    className="trackingCode"
                    type="secondary"
                    style={{
                      fontSize: "12px",
                      fontFamily: "monospace",
                      display: "block",
                      wordBreak: "break-all",
                    }}
                  >
                    Code: {activeSession.id}
                  </Text>
                </div>
              </div>

              <div
                style={{
                  background: "#fafafa",
                  padding: "12px",
                  borderRadius: "6px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: "13px" }}>
                    Target Completion
                  </Text>
                  <Text strong style={{ color: "#1677ff", fontSize: "14px" }}>
                    {progressPercentage}%
                  </Text>
                </div>
                <Progress
                  percent={progressPercentage}
                  showInfo={false}
                  size="small"
                  strokeColor="#1677ff"
                />
              </div>

              {isOrderedRoute &&
                currentPoint &&
                (() => {
                  const isInside = isInsidePointRadius(currentPoint);
                  const isActive =
                    activeVisitPointId === currentPoint.route_point_id;
                  const visualState = isActive
                    ? "active"
                    : isInside
                      ? "inside"
                      : "outside";
                  const styles = targetStatusStyles[visualState];
                  return (
                    <div
                      style={{
                        background: styles.background,
                        border: styles.border,
                        borderRadius: "6px",
                        padding: "16px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <Text
                          strong
                          style={{
                            fontSize: "11px",
                            color: styles.headerColor,
                            letterSpacing: "0.5px",
                          }}
                        >
                          {isActive
                            ? "CURRENT ACTIVE VISIT"
                            : "CURRENT ACTIVE TARGET"}
                        </Text>
                        <Title
                          level={5}
                          style={{
                            margin: "4px 0 2px 0",
                            color: "#141414",
                            fontSize: "16px",
                          }}
                        >
                          {currentPoint?.name}
                        </Title>
                        {currentPoint?.address && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "13px",
                              wordBreak: "break-word",
                              lineHeight: "1.4",
                            }}
                            className="routeExecutionAddress"
                          >
                            {currentPoint?.address}
                          </Text>
                        )}
                        <div style={{ margin: "4px 0 8px 0" }}>
                          <Tag
                            color={
                              isActive ? "blue" : isInside ? "green" : "red"
                            }
                          >
                            {isActive
                              ? "Checked In"
                              : isInside
                                ? "Inside Radius"
                                : "Outside Radius"}
                          </Tag>
                        </div>

                        {/* FIXED: GRID CONTROL ALIGNMENT ROW */}
                        <Row gutter={[8, 8]} align="middle">
                          <Col span={14}>
                            {!activeVisitPointId ? (
                              <Button
                                type="primary"
                                block
                                icon={<CheckCircleOutlined />}
                                onClick={() => {
                                  setArrivalPoint(currentPoint);
                                  setArrivalPromptOpen(true);
                                }}
                                style={{
                                  background: "#52c41a",
                                  borderColor: "#52c41a",
                                  borderRadius: "6px",
                                }}
                              >
                                Check In
                              </Button>
                            ) : (
                              <Button
                                danger
                                type="primary"
                                block
                                icon={<StopOutlined />}
                                // disabled={!isActive}
                                onClick={() => {
                                  setExitPoint(currentPoint);
                                  setExitPromptOpen(true);
                                }}
                                style={{ borderRadius: "6px" }}
                              >
                                Check Out
                              </Button>
                            )}
                          </Col>
                          <Col span={6}>
                            <Button
                              type="default"
                              block
                              icon={<NavigationIcon />}
                              onClick={() => navigateToPoint(currentPoint)}
                              style={{ borderRadius: "6px" }}
                            />
                          </Col>
                          <Col span={4}>
                            <Tooltip title="View History Notes">
                              <Button
                                block
                                shape="default"
                                icon={<FileTextOutlined />}
                                onClick={() =>
                                  loadPointHistory(currentPoint, 1)
                                }
                                style={{ borderRadius: "6px" }}
                              />
                            </Tooltip>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  );
                })()}

              <div>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    {activeAdhocVisit ? (
                      <Button
                        danger
                        type="primary"
                        block
                        size="large"
                        onClick={() => setAdhocCheckoutModalOpen(true)}
                        style={{ height: "40px", borderRadius: "6px" }}
                      >
                        Checkout Visit
                      </Button>
                    ) : (
                      <Tooltip
                        title={
                          activeVisitPointId
                            ? "Checkout current route point first"
                            : isInsidePointRadius(currentPoint)
                              ? "You are already at an assigned route point"
                              : ""
                        }
                      >
                        <Button
                          icon={<EnvironmentOutlined />}
                          block
                          size="large"
                          disabled={
                            activeVisitPointId ||
                            isInsidePointRadius(currentPoint)
                          }
                          onClick={() => setAdhocModalOpen(true)}
                          style={{ height: "40px", borderRadius: "6px" }}
                        >
                          New Adhoc
                        </Button>
                      </Tooltip>
                    )}
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size="large"
                      onClick={() => {
                        if (activeVisitPointId)
                          return message.warning(
                            "Please checkout the current route point first.",
                          );
                        if (activeAdhocVisit)
                          return message.warning(
                            "Please checkout the active visit first.",
                          );
                        setRouteCompleteModalOpen(true);
                      }}
                      style={{ height: "40px", borderRadius: "6px" }}
                    >
                      {completeRouteLabel}
                    </Button>
                  </Col>
                </Row>
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                    gap: "12px",
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: "#434343",
                      letterSpacing: "0.3px",
                    }}
                  >
                    WAYPOINT TRACK TIMELINE
                  </Text>
                  <Tag
                    color="default"
                    style={{ margin: 0, fontSize: "11px", fontWeight: 500 }}
                  >
                    {completedPoints}/{totalPoints} Stops
                  </Tag>
                </div>

                <List
                  header={null}
                  bordered
                  dataSource={routePoints}
                  style={{
                    maxHeight: isMobile ? "280px" : "calc(100vh - 580px)",
                    overflowY: "auto",
                    background: "#fff",
                    borderRadius: "6px",
                  }}
                  renderItem={(item, index) => {
                    const isCompleted = completedPointIds.includes(
                      item.route_point_id,
                    );
                    const isNearby =
                      isInsidePointRadius(item) &&
                      !isCompleted &&
                      !activeVisitPointId;
                    const isActive = activeVisitPointId === item.route_point_id;
                    const rowState = isCompleted
                      ? "completed"
                      : isActive
                        ? "active"
                        : isNearby
                          ? "inside"
                          : "pending";
                    const styles = targetStatusStyles[rowState];
                    return (
                      <List.Item
                        style={{
                          padding: "12px 10px",
                          background: styles.background,
                          borderBottom: "1px solid #f0f0f0",
                          borderLeft:
                            isActive || isNearby
                              ? `4px solid ${styles.headerColor}`
                              : "none",
                          flexDirection: "column",
                          alignItems: "stretch",
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "8px",
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: "13px",
                              wordBreak: "break-word",
                              flex: 1,
                              color: "#262626",
                            }}
                          >
                            {index + 1}. {item.name}{" "}
                            {isActive && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  color: "#1677ff",
                                  fontSize: "11px",
                                }}
                              >
                                ● Active
                              </span>
                            )}
                          </Text>
                          <Space size={4}>
                            <Tooltip title="View History Notes">
                              <Button
                                shape="circle"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={() => loadPointHistory(item, 1)}
                              />
                            </Tooltip>
                            <Button
                              shape="circle"
                              size="small"
                              icon={<NavigationIcon />}
                              onClick={() => navigateToPoint(item)}
                            />
                          </Space>
                          <Tag
                            color={
                              isCompleted
                                ? "green"
                                : isActive
                                  ? "cyan"
                                  : isNearby
                                    ? "gold"
                                    : "blue"
                            }
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              flexShrink: 0,
                            }}
                          >
                            {isCompleted
                              ? "Completed"
                              : isActive
                                ? "Checked In"
                                : isNearby
                                  ? "Nearby"
                                  : "Pending"}
                          </Tag>
                        </div>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: "11px",
                            display: "block",
                            marginTop: 4,
                            wordBreak: "break-word",
                            lineHeight: "1.4",
                          }}
                          className="routeExecutionAddress"
                        >
                          {item.address}
                        </Text>
                        {!isOrderedRoute && isNearby && (
                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              background: "#fff",
                              padding: "6px 8px",
                              borderRadius: "4px",
                              border: "1px solid #ffd666",
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              Within range
                            </Text>
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => {
                                setArrivalPoint(item);
                                setArrivalPromptOpen(true);
                              }}
                            >
                              Check In
                            </Button>
                          </div>
                        )}
                        {!isOrderedRoute &&
                          activeVisitPointId === item.route_point_id && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: "#fff",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                border: "1px solid #91caff",
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{ fontSize: "11px" }}
                              >
                                Visit active
                              </Text>
                              <Button
                                danger
                                size="small"
                                onClick={() => {
                                  setExitPoint(item);
                                  setExitPromptOpen(true);
                                }}
                              >
                                Check Out
                              </Button>
                            </div>
                          )}
                      </List.Item>
                    );
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={14} lg={15}>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              defaultCenter={center || { lat: 20, lng: 0 }}
              zoom={13}
              onLoad={(m) => setMap(m)}
            >
              {currentPosition && (
                <Marker
                  position={currentPosition}
                  icon={{
                    url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                  }}
                />
              )}
              {routePoints.map((point, index) => {
                const isCompleted = completedPointIds.includes(
                  point.route_point_id,
                );
                const isActive = activeVisitPointId === point.route_point_id;
                return (
                  <React.Fragment key={point.route_point_id}>
                    <Marker
                      position={{
                        lat: Number(point.latitude),
                        lng: Number(point.longitude),
                      }}
                      title={`${index + 1}. ${point.name}`}
                      label={{
                        text: String(index + 1),
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                      icon={
                        isActive
                          ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png"
                          : isCompleted
                            ? "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
                            : undefined
                      }
                    />
                    <Circle
                      center={{
                        lat: Number(point.latitude),
                        lng: Number(point.longitude),
                      }}
                      radius={Number(point.radius_meters)}
                      options={{
                        fillColor: isActive ? "#1ac21a" : "#1890ff",
                        fillOpacity: 0.15,
                        strokeColor: isActive ? "#52c41a" : "#1890ff",
                        weight: 1,
                        clickable: false,
                      }}
                    />
                  </React.Fragment>
                );
              })}
              {adhocVisits.map((visit) => (
                <Marker
                  key={visit?.id}
                  position={{
                    lat: Number(visit?.latitude),
                    lng: Number(visit?.longitude),
                  }}
                  title={visit?.visit_name}
                  label={{
                    text:
                      visit.visit_name.length > 10
                        ? `${visit.visit_name.slice(0, 10)}...`
                        : visit.visit_name,
                    color:
                      visit.status === "checked_in" ? "#ff9800" : "#28a745",
                    fontSize: "11px",
                  }}
                />
              ))}
              <Polyline
                path={routePoints.map((p) => ({
                  lat: Number(p.latitude),
                  lng: Number(p.longitude),
                }))}
                options={{
                  strokeColor: "#1890ff",
                  strokeOpacity: 1,
                  strokeWeight: 4,
                }}
              />
            </GoogleMap>
          )}
        </Col>
      </Row>

      <Modal
        open={arrivalPromptOpen}
        title="Arrived at Route Point"
        onOk={() => handleCheckin(arrivalPoint)}
        confirmLoading={loading} // ⏳ This disables the OK button and shows an AntD spinner when loading is true
        onCancel={() => {
          if (loading) return; // Prevent closing the window mid-request
          setArrivalPromptOpen(false);
          setCheckinNotes("");
        }}
        destroyOnClose
        style={{ top: 40 }}
      >
        <p>
          You arrived at: <strong>{arrivalPoint?.name}</strong>
        </p>
        <Input.TextArea
          rows={4}
          value={checkinNotes}
          onChange={(e) => setCheckinNotes(e.target.value)}
          placeholder="Check-in note"
          style={{ marginTop: 12 }}
          disabled={loading} // Disable text box while sending
        />
      </Modal>

      <Modal
        open={exitPromptOpen}
        title="Left Route Point"
        onCancel={() => {
          setExitPromptOpen(false);
          setCheckoutNotes("");
        }}
        style={{ top: 40 }}
        footer={[
          <Button
            key="ignore"
            onClick={() => {
              setExitPromptOpen(false);
              setCheckoutNotes("");
            }}
          >
            Ignore
          </Button>,
          <Button
            key="checkout"
            type="primary"
            danger
            onClick={() => handleCheckout(exitPoint)}
          >
            Checkout Now
          </Button>,
        ]}
      >
        <p>
          You left: <strong>{exitPoint?.name}</strong>
        </p>
        <p style={{ marginTop: 10 }}>Would you like to checkout?</p>
        <Input.TextArea
          rows={4}
          value={checkoutNotes}
          onChange={(e) => setCheckoutNotes(e.target.value)}
          placeholder="Checkout note"
          style={{ marginTop: 12 }}
        />
      </Modal>

      <Modal
        open={adhocModalOpen}
        title="New Customer Visit"
        onCancel={() => setAdhocModalOpen(false)}
        onOk={handleAdhocCheckin}
        destroyOnClose
        style={{ top: 40 }}
      >
        <Form form={adhocForm} layout="vertical">
          <Form.Item
            label="Visit Name"
            name="visit_name"
            rules={[{ required: true, message: "Please input visit name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Check In Notes" name="checkin_notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={adhocCheckoutModalOpen}
        title="Checkout Visit"
        onCancel={() => setAdhocCheckoutModalOpen(false)}
        onOk={handleAdhocCheckout}
        destroyOnClose
        style={{ top: 40 }}
      >
        <Form form={adhocCheckoutForm} layout="vertical">
          <Form.Item label="Checkout Notes" name="checkout_notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={adhocExitPromptOpen}
        title="Left Adhoc Visit Location"
        onCancel={() => {
          setAdhocExitPromptOpen(false);
          setAdhocCheckoutNotes("");
        }}
        style={{ top: 40 }}
        footer={[
          <Button
            key="ignore"
            onClick={() => {
              setAdhocExitPromptOpen(false);
            }}
          >
            Ignore
          </Button>,
          <Button
            key="checkout"
            type="primary"
            danger
            onClick={handleAutoAdhocCheckout}
          >
            Checkout Now
          </Button>,
        ]}
      >
        <p>
          You left: <strong>{activeAdhocVisit?.visit_name}</strong>
        </p>
        <Input.TextArea
          rows={4}
          value={adhocCheckoutNotes}
          onChange={(e) => setAdhocCheckoutNotes(e.target.value)}
          placeholder="Checkout note"
        />
      </Modal>

      <Modal
        open={routeCompleteModalOpen}
        title={
          allPointsCompleted
            ? isFlexibleAssignment
              ? "🎉 All Points Visited Today"
              : "🎉 Route Completed"
            : completeRouteLabel
        }
        onCancel={() => setRouteCompleteModalOpen(false)}
        style={{ top: 20 }}
        width={520} // Slightly wider width container to hold longer strings safely
        footer={
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
            <Button 
              type="primary" 
              block
              style={{ height: "40px", borderRadius: "6px" }}
              onClick={async () => {
                try {
                  await handleCompleteRoute(true);
                  setRouteCompleteModalOpen(false);
                } catch (err) {
                  console.error(err);
                }
              }}
            >
              {completeRouteLabel} + Attendance Checkout
            </Button>
            <div style={{ display: "flex", gap: "8px", width: "100%" }}>
              <Button
                style={{ flex: 1, height: "36px", borderRadius: "6px" }}
                onClick={async () => {
                  try {
                    await handleCompleteRoute(false);
                    setRouteCompleteModalOpen(false);
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >
                {completeRouteLabel} Only
              </Button>
              <Button 
                style={{ flex: 1, height: "36px", borderRadius: "6px" }}
                onClick={() => setRouteCompleteModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Card size="small">
            <Row gutter={16} style={{ textAlign: "center" }}>
              <Col span={8}>
                <Text strong>Assigned</Text>
                <div>{totalPoints}</div>
              </Col>
              <Col span={8}>
                <Text strong>Visited</Text>
                <div>{completedPoints}</div>
              </Col>
              <Col span={8}>
                <Text strong>Missed</Text>
                <div>{missedPoints}</div>
              </Col>
            </Row>
          </Card>
          <Progress percent={completionPercentage} />
          <Card size="small">
            <div>
              <strong>Adhoc Visits:</strong>{" "}
              {adhocVisits.filter((v) => v.status === "checked_out").length}
            </div>
          </Card>
          {missedPoints > 0 ? (
            assignment?.schedule_type === "flexible" ? (
              // Flexible assignments are a pool of points worked through at
              // the employee's own pace (e.g. a shop round) -- not visiting
              // all of them in one day is expected, not a shortfall, so this
              // reads as informational rather than a warning.
              <Alert
                type="info"
                showIcon
                message={`${missedPoints} route point(s) not visited yet -- you can visit them another day.`}
              />
            ) : (
              <Alert
                type="warning"
                showIcon
                message={`You still have ${missedPoints} unvisited route point(s).`}
              />
            )
          ) : (
            <Alert
              type="success"
              showIcon
              message="All assigned route points have been completed."
            />
          )}
        </Space>
      </Modal>

      {/* REFACTORED PAGINATED VISITATION HISTORY STREAM MODAL */}
      <Modal
        open={historyOpen}
        title={
          <span>
            Visit History -{" "}
            <span style={{ color: "#1677ff", textTransform: "capitalize" }}>
              {selectedPoint?.name || "Customer"}
            </span>
            <span
              style={{
                marginLeft: 12,
                fontSize: "14px",
                fontWeight: "normal",
                color: "#8c8c8c",
              }}
            >
              (Total Visits: <strong>{historyPagination.total}</strong>)
            </span>
          </span>
        }
        footer={null}
        width={700}
        onCancel={() => {
          setHistoryOpen(false);
          setPointHistory([]);
          setSelectedPoint(null);
          setHistoryPagination({ current: 1, pageSize: 5, total: 0 });
        }}
      >
        <Spin spinning={historyLoading}>
          <List
            dataSource={pointHistory}
            locale={{
              emptyText: <Empty description="No visit history found" />,
            }}
            pagination={{
              current: historyPagination.current,
              pageSize: historyPagination.pageSize,
              total: historyPagination.total,
              size: "small",
              onChange: (page) => loadPointHistory(selectedPoint, page),
            }}
            renderItem={(item) => (
              <List.Item style={{ padding: "16px 8px" }}>
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f0f0f0",
                      paddingBottom: "4px",
                      marginBottom: "8px",
                    }}
                  >
                    <Text strong>
                      {formatToCompanyTimezone(
                        item.entered_at || item.checkin_time,
                      )}
                    </Text>
                    {item.duration_minutes && (
                      <Tag color="blue">{item.duration_minutes} Mins</Tag>
                    )}
                  </div>
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                        Check-In Note
                      </div>
                      <div
                        style={{
                          background: "#fafafa",
                          padding: "8px",
                          borderRadius: "4px",
                          minHeight: "36px",
                          marginTop: "4px",
                        }}
                      >
                        {item.checkin_notes || (
                          <Text type="secondary" italic>
                            -
                          </Text>
                        )}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                        Check-Out Note
                      </div>
                      <div
                        style={{
                          background: "#fafafa",
                          padding: "8px",
                          borderRadius: "4px",
                          minHeight: "36px",
                          marginTop: "4px",
                        }}
                      >
                        {item.checkout_notes || (
                          <Text type="secondary" italic>
                            -
                          </Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </List.Item>
            )}
          />
        </Spin>
      </Modal>
    </div>
  );
};

export default RouteExecution;
